import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const defaultDatumIds = [
  "base_wall_bottom",
  "base_wall_top",
  "gable_end_bottom",
  "gable_end_top",
  "wall_start",
  "wall_end",
];

function datumName(facadeName: string, datumName: string): string {
  return facadeName + ": " + datumName;
}

function datumsEq(d1, d2): boolean {
  const eq =
    (d1.datumType || d1.type === d2.datumType || d2.type) &&
    d1.anchor === d2.anchor &&
    d1.justification === d2.justification &&
    d1.offset === d2.offset &&
    d1.offsetNormalized === d2.offsetNormalized;
  // if (!eq) {
  //   console.log({ d1, d2 });
  // }
  return eq;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_280: DocMigration = {
  maxSchemaVersion: "0.0.280",
  description: "Flatten facade datum, sweep stratum, and span stratum",
  apply(json: any) {
    // facade datums will be added to the datum catalog
    const facadeDatums = {} as any;

    const docFacadeSpanStratum = {} as any;
    const docFacadeSweepStratum = {} as any;
    for (const defaultDatumId of defaultDatumIds) {
      switch (defaultDatumId) {
        case "base_wall_bottom":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "base_wall_bottom",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "VERTICAL",
            anchor: "BASE_WALL",
            justification: "BOTTOM_UP",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
        case "base_wall_top":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "base_wall_top",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "VERTICAL",
            anchor: "BASE_WALL",
            // justification: "TOP_UP",
            justification: "TOP_DOWN",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
        case "gable_end_bottom":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "gable_end_bottom",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "VERTICAL",
            anchor: "GABLE_END",
            justification: "BOTTOM_UP",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
        case "gable_end_top":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "gable_end_top",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "VERTICAL",
            anchor: "GABLE_END",
            // justification: "TOP_UP",
            justification: "TOP_DOWN",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
        case "wall_start":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "wall_start",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "HORIZONTAL",
            anchor: "WALL_START",
            justification: "START_TO_END",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
        case "wall_end":
          facadeDatums[defaultDatumId] = {
            id: defaultDatumId,
            name: "wall_end",
            sortIndex: 0,
            entityType: "FACADE_DATUM",
            datumType: "HORIZONTAL",
            anchor: "WALL_END",
            justification: "END_TO_START",
            offset: 0,
            offsetNormalized: false,
            editable: false,
          };
          break;
      }
    }
    json.facadeSpanStratum = {};
    json.facadeSweepStratum = {};
    for (const facadeId in json.facades) {
      const tempFacadeDatums = {} as any;
      defaultDatumIds.forEach((defaultDatumId) => {
        tempFacadeDatums[defaultDatumId] = facadeDatums[defaultDatumId];
      });

      const facade = json.facades[facadeId];

      for (const datumId in facade.datumTable) {
        const datum = facade.datumTable[datumId];

        // it is possible that the datum in the datum table
        // has a default id but is not equal to the default datum
        // in this case a new datum should be created
        const shouldNew = defaultDatumIds.includes(datumId) ? !datumsEq(datum, facadeDatums[datumId]) : true;

        if (shouldNew) {
          const id = uid();
          tempFacadeDatums[id] = {
            ...datum,
            id,
            datumType: datum.type,
            parentId: facadeId,
            oldId: datumId,
            name: datumName(facade.name, datum.name),
          };
        }
      }
      delete facade.datumTable;

      for (const tempId in tempFacadeDatums) {
        const tempDatum = tempFacadeDatums[tempId];

        delete tempDatum.type;

        facadeDatums[tempId] = { ...tempDatum };
      }

      let sortIndex = 0;
      for (const spanStratumId in facade.spanStrata) {
        const id = uid();

        const stratum = facade.spanStrata[spanStratumId];

        const { upperDatum, lowerDatum, startDatum, endDatum } = stratum;

        const upperName = datumName(facade.name, upperDatum);
        const lowerName = datumName(facade.name, lowerDatum);
        const startName = datumName(facade.name, startDatum);
        const endName = datumName(facade.name, endDatum);

        const findUpperDatum =
          (
            Object.values(tempFacadeDatums).find(
              (datum: any) => datum.oldId === upperDatum || datum.name === upperName,
            ) as any
          )?.id || upperDatum;
        const findLowerDatum =
          (
            Object.values(tempFacadeDatums).find(
              (datum: any) => datum.oldId === lowerDatum || datum.name === lowerName,
            ) as any
          )?.id || lowerDatum;
        const findStartDatum =
          (
            Object.values(tempFacadeDatums).find(
              (datum: any) => datum.oldId === startDatum || datum.name === startName,
            ) as any
          )?.id || startDatum;
        const findEndDatum =
          (
            Object.values(tempFacadeDatums).find(
              (datum: any) => datum.oldId === endDatum || datum.name === endName,
            ) as any
          )?.id || endDatum;
        docFacadeSpanStratum[id] = {
          ...stratum,
          id,
          parentId: facadeId,
          upperDatum: findUpperDatum,
          lowerDatum: findLowerDatum,
          startDatum: findStartDatum,
          endDatum: findEndDatum,
          sortIndex: sortIndex++,
        };
      }
      delete facade.spanStrata;

      sortIndex = 0;
      for (const sweepStratumId in facade.sweepStrata) {
        const id = uid();
        const { datum: sweepDatum } = facade.sweepStrata[sweepStratumId];
        const matchName = datumName(facade.name, sweepDatum);
        const findDatumId =
          (Object.values(facadeDatums).find((datum: any) => datum.name === matchName) as any)?.id || sweepDatum;
        const sweepStratum = facade.sweepStrata[sweepStratumId];
        const findDatum = facadeDatums[findDatumId];

        docFacadeSweepStratum[id] = {
          ...sweepStratum,
          datumType: findDatum?.type || findDatum?.datumType,
          id,
          parentId: facadeId,
          datum: findDatumId,
          sortIndex: sortIndex++,
        };
      }
      delete facade.sweepStrata;
    }
    json.facadeSpanStratum = { ...docFacadeSpanStratum };
    json.facadeSweepStratum = { ...docFacadeSweepStratum };

    if (Object.keys(facadeDatums).length) {
      let datumCatalog;
      let settingsSet;

      if (!json.settingsSets) {
        json.settingsSets = {};
      }

      if (!json.settings) {
        json.settings = {};
      } else {
        for (const settingId in json.settings) {
          const setting = json.settings[settingId];
          if (setting.type === "DATUM_CATALOG_SETTINGS") {
            datumCatalog = setting;
            break;
          }
        }
      }

      if (!datumCatalog) {
        // create a new setting set for the datum catalog
        const settingSetId = uid();
        settingsSet = {
          id: settingSetId,
          entityType: "SETTINGS_SET",
          name: "Datums Setting Set",
          sortIndex: 0,
        };
        json.settingsSets[settingSetId] = settingsSet;

        datumCatalog = {
          id: uid(),
          type: "DATUM_CATALOG_SETTINGS",
          entityType: "SETTINGS",
          name: "Datum Catalog Settings",
          sortIndex: 0,
          parentId: settingSetId,
          datums: {},
        };
        json.settings[datumCatalog.id] = datumCatalog;
      }

      datumCatalog.datums = { ...datumCatalog.datums, ...facadeDatums };
    }

    // console.log({ json });

    return json;
  },
};

export default migration_0_0_280;
